// What the jump bar's text names by itself, before any source is asked:
// every form the router reads (docs/superpowers/specs/2026-11-03-
// navigation-design.md, "Jump bar"), read through the router's own
// functions and the subject key's grammar, so the bar has no idea of its
// own of what a name means: nothing here splits a compare's ends or trims
// a name; the router reads the text, and a row is built from what it read.
//
//   a Changeling or GitHub URL, or an app path   what the router makes of its path
//   o/r#12, o/r@a...b, o/r:ref//path             a subject key (only that: a text shaped like one names nothing else)
//   o/r                                          the repository (a guess until a source knows it)
//   in the current repository:
//   #12                                          its pull request
//   ref//path                                    a history filtered to a path, as its key writes it
//   main...feat/x, ana:feat, feat/x, a SHA       `compare/<text>` as the router reads it
import { parseSubject } from '../api/relaySubject';
import { formerAlias, GH, githubAlias, matchRoute, parseSubjectPath, type Route } from '../router';
import { branchRow, repoRow, subjectRow, type Repo, type Row } from './rows';

export interface InterpretContext {
  /** The repository the page is about, which `#12` and branch names are read in; null on the app's own pages. */
  repo: Repo | null;
  /** The app's origin: a URL of it is read as the router reads its path. */
  origin: string;
  /** GitHub's web origin (`github.com`, or a GitHub Enterprise host): a URL of it is read with its host swapped for `/gh`. */
  githubWeb: string | null;
}

/** The repository a route's page is about, or null for the app's own pages. */
export function routeRepo(route: Route): Repo | null {
  if ('subject' in route) return { owner: route.subject.owner, repo: route.subject.repo };
  if (route.name === 'ref') return { owner: route.locator.owner, repo: route.locator.repo };
  if (route.name === 'repo') return { owner: route.owner, repo: route.repo };
  return null;
}

const host = (url: string | null): string | null => {
  if (!url) return null;
  try {
    return new URL(url).host.toLowerCase();
  } catch {
    return null;
  }
};

/** GitHub's hosts: github.com (with or without `www.`, by http or https), and the deployment's own. */
const isGitHub = (h: string, ctx: InterpretContext) => h === 'github.com' || h === 'www.github.com' || h === host(ctx.githubWeb) || h === 'www.' + host(ctx.githubWeb);

/** The app path a URL names (`undefined` when `text` is no URL; null when it is one of somewhere else). */
function urlPath(text: string, ctx: InterpretContext): { pathname: string; search: string } | null | undefined {
  if (text.startsWith('/')) {
    const at = new URL(text, 'http://app');
    return { pathname: at.pathname, search: at.search };
  }
  if (!/^https?:\/\//i.test(text)) return undefined;
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  if (url.origin === new URL(ctx.origin).origin) {
    // A hash-routed build keeps the app's path in the hash.
    if (url.hash.startsWith('#/')) {
      const inner = new URL(url.hash.slice(1), 'http://app');
      return { pathname: inner.pathname, search: inner.search };
    }
    return { pathname: url.pathname, search: url.search };
  }
  if (isGitHub(url.host.toLowerCase(), ctx)) return { pathname: GH + url.pathname, search: url.search };
  return null;
}

/** The row of the page at an app path: a subject's, a history's locator, a repository's, or the app's own page. */
function pathRows(pathname: string, search: string): Row[] {
  const alias = githubAlias(pathname, search) ?? formerAlias(pathname, search);
  const at = new URL(alias ?? pathname + search, 'http://app');
  const route = matchRoute(at.pathname, at.search);
  // What the router read, written back: one spelling (no trailing slash) for each place.
  const href = (path: string) => path + at.search;
  if ('subject' in route) return [subjectRow(route.subject, { href: href(at.pathname.replace(/(.)\/+$/, '$1')) })];
  switch (route.name) {
    case 'ref': {
      const l = route.locator;
      const title = l.path === undefined ? l.spec : `${l.spec} · ${l.path}`;
      return [{ id: `locator:${l.owner}/${l.repo}/${l.spec}${l.path === undefined ? '' : '//' + l.path}`.toLowerCase(), title, detail: `${l.owner}/${l.repo}`.toLowerCase(), actions: [{ kind: 'go', label: 'History', href: href(at.pathname.replace(/(.)\/+$/, '$1')) }] }];
    }
    case 'repo':
      return [repoRow({ owner: route.owner, repo: route.repo })];
    case 'not-found': {
      // A repository page the router doesn't read (a GitHub-only path): the repository, as the router reads its page.
      const m = new RegExp(`^${GH}/([^/]+)/([^/]+)/?$`).exec(route.path);
      return m ? repoRows(m[1], m[2]) : [];
    }
    default:
      return [{ id: `page:${at.pathname}`, title: at.pathname, actions: [{ kind: 'go', label: 'Open', href: at.pathname + at.search }] }];
  }
}

/** `owner/repo` as a repository, when the router reads it as one; a guess a source that knows the repository replaces. */
function repoRows(owner: string, repo: string): Row[] {
  const route = matchRoute(`${GH}/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}`);
  return route.name === 'repo' ? [{ ...repoRow({ owner: route.owner, repo: route.repo }), provisional: true }] : [];
}

const rowOf = (s: NonNullable<ReturnType<typeof parseSubject>>) => subjectRow(s);

/** Text shaped like a subject key (`o/r#…`, `o/r@…`, `o/r:…`): read as a key, and nothing else. */
const KEY_SHAPED = /^[^/\s#@:]+\/[^/\s#@:]+[#@:]/;

/** What `t` names in the current repository. */
function inRepo(t: string, r: Repo): Row[] {
  if (t.startsWith('#') || t.includes('//')) {
    const s = parseSubject(`${r.owner}/${r.repo}${t.startsWith('#') ? '' : ':'}${t}`);
    return s ? [rowOf(s)] : [];
  }
  // The router reads the text as GitHub's compare URL would hold it: a compare, a fork's head, or a branch, tag or SHA.
  const at = parseSubjectPath(`${GH}/${encodeURIComponent(r.owner)}/${encodeURIComponent(r.repo)}/compare/${t.split('/').map(encodeURIComponent).join('/')}`);
  if (!at || !('subject' in at) || at.subject.kind !== 'compare') return [];
  const s = at.subject;
  if (s.base !== null || s.headRepo !== null) return [subjectRow(s)];
  // A branch, a tag or a commit, named as the router read it: what the repository says of it (the branch source) takes this row's place.
  return [{ ...branchRow(r, s.head, { defaultBranch: null, pull: null }), provisional: true }];
}

/** The rows `text` names by itself, the most specific first: a URL or a subject key, then what it names in the current repository, then a repository. */
export function interpret(text: string, ctx: InterpretContext): Row[] {
  const t = text.trim();
  if (!t || /\s/.test(t)) return [];
  const url = urlPath(t, ctx);
  if (url !== undefined) return url ? pathRows(url.pathname, url.search) : [];
  if (KEY_SHAPED.test(t)) {
    const key = parseSubject(t);
    return key ? [rowOf(key)] : [];
  }
  const out = ctx.repo ? inRepo(t, ctx.repo) : [];
  // A repository too, unless the repository's own reading named something for certain (a compare, a fork's head).
  const two = /^([^/]+)\/([^/]+)$/.exec(t);
  if (two && out.every((r) => r.provisional)) out.push(...repoRows(two[1], two[2]));
  return out;
}
